import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  ChevronRight,
  ArrowUpRight,
  Building2,
  ClipboardCheck,
  FileCheck2,
  Ruler,
  ShieldCheck,
  MapPinned,
  Factory,
  Layers3,
  Route,
  BadgeCheck,
  Landmark,
  BriefcaseBusiness,
  Hammer,
} from "lucide-react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const heroBg = "/assets/images/bg2.jpeg";
const sectionBg = "/assets/images/bg2.jpeg";
const portalLink = "/portal/login?service=engineering";

const focusAreas = [
  {
    title: "Development Planning",
    text: "Guidance for investors planning physical development, site use, building works and infrastructure projects within OGFZA Free Zones.",
    icon: MapPinned,
  },
  {
    title: "Infrastructure Review",
    text: "Support for technical submissions connected to industrial facilities, warehouses, tank farms, utilities and other zone infrastructure.",
    icon: Layers3,
  },
  {
    title: "Construction Compliance",
    text: "Service access for inspections, monitoring, site verification and development control during approved project execution.",
    icon: ShieldCheck,
  },
];

const serviceAccess = [
  {
    title: "Development Application",
    text: "For investors preparing to commence approved physical development inside a Free Zone.",
    icon: Building2,
  },
  {
    title: "Technical Document Upload",
    text: "For submitting engineering drawings, layouts, specifications and supporting project documents.",
    icon: FileCheck2,
  },
  {
    title: "Inspection Request",
    text: "For site verification, progress inspection, project review and completion-related visits.",
    icon: ClipboardCheck,
  },
  {
    title: "Contractor & Consultant Access",
    text: "For contractors and consultants supporting approved development activities in the zones.",
    icon: BriefcaseBusiness,
  },
];

const supportedProjects = [
  "Industrial facilities",
  "Warehouses and logistics buildings",
  "Tank farms and storage facilities",
  "Office and administrative buildings",
  "Roads, bridges and utilities",
  "Jetties and marine infrastructure",
  "Water treatment plants",
  "Temporary structures and fences",
];

const investorJourney = [
  {
    title: "Plan",
    text: "Investor prepares the intended development, facility type and site information.",
  },
  {
    title: "Submit",
    text: "Required engineering documents and application details are submitted through the portal.",
  },
  {
    title: "Review",
    text: "The Engineering Service reviews submissions for planning, safety, infrastructure and compliance alignment.",
  },
  {
    title: "Inspect",
    text: "Site verification and project inspections are carried out where required.",
  },
  {
    title: "Approve",
    text: "Approved requests proceed to permits, clearances or completion-related certification.",
  },
];

const Engineering = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.8,
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.2,
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        delay: 0.2,
        duration: 1,
      });

      gsap.utils.toArray(".reveal-up").forEach((el) => {
        gsap.from(el, {
          opacity: 0,
          y: 55,
          duration: 0.85,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 86%",
          },
        });
      });

      gsap.utils.toArray(".card-animate").forEach((el, index) => {
        gsap.from(el, {
          opacity: 0,
          y: 45,
          delay: index * 0.05,
          duration: 0.75,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 90%",
          },
        });
      });

      gsap.to(".floating-orb-1", {
        y: 45,
        x: -25,
        duration: 7,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".floating-orb-2", {
        y: -45,
        x: 25,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main ref={pageRef} className="bg-white overflow-hidden">
      <section className="relative min-h-screen flex items-center overflow-hidden text-white">
        <img
          src={heroBg}
          alt="OGFZA Engineering Service"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-[#041018]/85" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#082567]/95 via-[#026B07]/70 to-[#F2AF11]/30" />

        <div className="floating-orb-1 absolute top-0 left-0 w-[520px] h-[520px] rounded-full bg-[#082567]/45 blur-3xl" />
        <div className="floating-orb-2 absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-[#F2AF11]/20 blur-3xl" />

        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:80px_80px]" />

        <div className="relative max-w-7xl mx-auto px-6 py-32">
          <div className="hero-badge inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-md mb-8">
            <span className="w-2 h-2 rounded-full bg-[#F2AF11]" />
            OGFZA Service Area
          </div>

          <h1
            className="hero-title text-6xl md:text-8xl font-black leading-[0.95] max-w-5xl"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            Engineering Service
          </h1>

          <p className="hero-text mt-8 max-w-3xl text-white/85 text-lg leading-[1.9]">
            Supporting safe, orderly and sustainable infrastructure development
            within Oil and Gas Free Zones through engineering review, site
            coordination, construction compliance and project certification
            access.
          </p>

          <div className="hero-text flex flex-wrap gap-4 mt-10">
            <Link
              to={portalLink}
              className="inline-flex items-center gap-3 bg-[#F2AF11] text-[#082567] px-7 py-4 rounded-full font-black hover:bg-white transition-all duration-300"
            >
              Open Engineering Portal
              <ArrowUpRight size={18} />
            </Link>

            <Link
              to="/contact-help/customer-service"
              className="inline-flex items-center gap-3 bg-white/10 border border-white/20 backdrop-blur-md text-white px-7 py-4 rounded-full font-black hover:bg-white hover:text-[#082567] transition-all duration-300"
            >
              Contact Support
              <ArrowUpRight size={18} />
            </Link>
          </div>

          <div className="hero-text flex items-center gap-2 mt-8 text-sm text-white/75">
            <Link
              to="/"
              className="flex items-center gap-1 hover:text-[#F2AF11] transition-colors"
            >
              <Home size={14} />
              Home
            </Link>

            <ChevronRight size={14} />

            <Link
              to="/services"
              className="hover:text-[#F2AF11] transition-colors"
            >
              Services
            </Link>

            <ChevronRight size={14} />

            <span className="text-[#F2AF11]">Engineering</span>
          </div>
        </div>
      </section>

      <section className="relative -mt-24 z-20 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {focusAreas.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="card-animate group bg-white rounded-[34px] p-8 border border-slate-200 shadow-[0_24px_80px_-50px_rgba(8,37,103,0.55)] hover:-translate-y-2 hover:border-[#F2AF11]/70 transition-all duration-500"
                >
                  <div className="w-16 h-16 rounded-[24px] bg-[#082567] text-white flex items-center justify-center mb-7 group-hover:bg-[#F2AF11] group-hover:text-[#082567] transition-all duration-500">
                    <Icon size={28} />
                  </div>

                  <h3
                    className="text-2xl font-black text-[#082567] mb-4"
                    style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                  >
                    {item.title}
                  </h3>

                  <p className="text-slate-600 leading-[1.8] text-[15.5px]">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-white overflow-hidden">
        <div className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full bg-[#F2AF11]/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-[#026B07]/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[0.9fr_1.1fr] gap-16 items-center">
          <div className="relative reveal-up">
            <div className="absolute -top-6 -left-6 w-28 h-28 bg-[#F2AF11] rounded-[30px]" />
            <div className="absolute -bottom-6 -right-6 w-28 h-28 bg-[#026B07] rounded-full" />

            <div className="relative rounded-[38px] overflow-hidden bg-white border border-slate-200 p-3 shadow-[0_30px_90px_-55px_rgba(8,37,103,0.7)]">
              <img
                src={sectionBg}
                alt="OGFZA Engineering Infrastructure"
                className="w-full h-[520px] object-cover rounded-[30px]"
              />

              <div className="absolute inset-3 rounded-[30px] bg-gradient-to-t from-[#041018]/80 via-[#041018]/10 to-transparent" />

              <div className="absolute bottom-8 left-8 right-8">
                <div className="inline-flex items-center gap-2 bg-white text-[#082567] px-5 py-3 rounded-full font-black shadow-lg">
                  <BadgeCheck size={18} className="text-[#026B07]" />
                  Infrastructure Development Support
                </div>
              </div>
            </div>
          </div>

          <div className="reveal-up">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Service Overview
            </p>

            <h2
              className="text-4xl md:text-5xl font-black leading-tight text-[#082567] mb-6"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              A Technical Gateway for Free Zone Development Projects
            </h2>

            <div className="w-24 h-1.5 bg-[#F2AF11] rounded-full mb-7" />

            <div className="space-y-5 text-slate-700 leading-[1.9] text-[16px]">
              <p>
                The Engineering Service supports investors and enterprises
                planning physical development within OGFZA-regulated Free Zones.
                It provides a clear access point for engineering-related
                applications, technical submissions, inspections and
                development approvals.
              </p>

              <p>
                Through this service area, investors can understand the
                engineering support available on the main website and proceed to
                the dedicated Engineering Portal for formal submissions,
                document uploads and request tracking.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-9">
              <div className="rounded-[24px] bg-[#FAFBFC] border border-slate-200 p-6">
                <Landmark className="text-[#026B07] mb-4" size={28} />
                <h3 className="font-black text-[#082567] text-lg">
                  Public Guidance
                </h3>
                <p className="text-slate-600 text-sm leading-[1.7] mt-2">
                  Clear website information before portal submission.
                </p>
              </div>

              <div className="rounded-[24px] bg-[#082567] p-6 text-white">
                <Route className="text-[#F2AF11] mb-4" size={28} />
                <h3 className="font-black text-lg">Portal Workflow</h3>
                <p className="text-white/75 text-sm leading-[1.7] mt-2">
                  Central login with a dedicated Engineering dashboard.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#FAFBFC]">
        <div className="absolute inset-0 opacity-[0.04] bg-[linear-gradient(to_right,#082567_1px,transparent_1px),linear-gradient(to_bottom,#082567_1px,transparent_1px)] bg-[size:70px_70px]" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-12 items-start">
            <div className="reveal-up lg:sticky lg:top-28">
              <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
                Digital Access
              </p>

              <h2
                className="text-4xl md:text-5xl font-black text-[#082567] leading-tight"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                Engineering Services Available Through the Portal
              </h2>

              <p className="text-slate-600 leading-[1.9] mt-6">
                The main website introduces the Engineering Service area, while
                the portal handles applications, uploaded documents, inspection
                requests, approvals and service tracking.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {serviceAccess.map((item, index) => {
                const Icon = item.icon;

                return (
                  <div
                    key={index}
                    className="card-animate rounded-[32px] bg-white p-8 border border-slate-200 hover:-translate-y-2 hover:border-[#F2AF11]/70 hover:shadow-[0_24px_80px_-58px_rgba(8,37,103,0.6)] transition-all duration-500"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-[#F2AF11]/15 text-[#082567] flex items-center justify-center mb-6 border border-[#F2AF11]/25">
                      <Icon size={26} />
                    </div>

                    <h3
                      className="text-2xl font-black text-[#082567] leading-tight mb-4"
                      style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                    >
                      {item.title}
                    </h3>

                    <p className="text-slate-600 leading-[1.8] text-[15.5px]">
                      {item.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-white overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-1/2 bg-[#082567]" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-0 rounded-[38px] overflow-hidden shadow-[0_30px_90px_-60px_rgba(8,37,103,0.7)]">
            <div className="bg-[#082567] text-white p-8 md:p-12">
              <p className="text-[#F2AF11] font-black tracking-[0.2em] uppercase text-xs mb-5">
                Project Coverage
              </p>

              <h2
                className="text-4xl md:text-5xl font-black leading-tight"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                Built for Investors Developing Physical Assets
              </h2>

              <p className="text-white/75 leading-[1.9] mt-6">
                Engineering support applies to investors and enterprises
                developing approved facilities, industrial assets and supporting
                infrastructure within Oil and Gas Free Zones.
              </p>

              <Link
                to={portalLink}
                className="inline-flex items-center gap-3 bg-[#F2AF11] text-[#082567] px-7 py-4 rounded-full font-black hover:bg-white transition-all duration-300 mt-8"
              >
                Continue to Portal
                <ArrowUpRight size={18} />
              </Link>
            </div>

            <div className="bg-white p-8 md:p-12 grid sm:grid-cols-2 gap-4">
              {supportedProjects.map((item, index) => (
                <div
                  key={index}
                  className="card-animate rounded-[24px] border border-slate-200 p-5 hover:border-[#F2AF11]/70 hover:-translate-y-1 transition-all duration-500"
                >
                  <Factory className="text-[#026B07] mb-4" size={24} />
                  <p className="text-[#082567] font-black leading-tight">
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#FAFBFC] overflow-hidden">
        <div className="absolute -top-24 right-0 w-[520px] h-[520px] bg-[#F2AF11]/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-4xl reveal-up mb-16">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Investor Pathway
            </p>

            <h2
              className="text-4xl md:text-5xl font-black text-[#082567] leading-tight"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              From Development Intention to Engineering Approval Access
            </h2>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-[42px] left-0 right-0 h-1 bg-slate-200" />

            <div className="grid md:grid-cols-5 gap-6">
              {investorJourney.map((step, index) => (
                <div key={index} className="card-animate relative">
                  <div className="w-20 h-20 rounded-full bg-[#082567] text-white border-[6px] border-[#FAFBFC] flex items-center justify-center font-black text-xl mb-6 relative z-10">
                    {index + 1}
                  </div>

                  <div className="rounded-[28px] bg-white border border-slate-200 p-6 h-full hover:-translate-y-2 hover:border-[#F2AF11]/70 transition-all duration-500">
                    <h3
                      className="text-2xl font-black text-[#082567] mb-3"
                      style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                    >
                      {step.title}
                    </h3>

                    <p className="text-slate-600 leading-[1.8] text-sm">
                      {step.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-[40px] bg-[#082567] p-8 md:p-12 shadow-[0_26px_90px_-60px_rgba(8,37,103,0.65)]">
            <div className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full bg-[#F2AF11]/20 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-[380px] h-[380px] rounded-full bg-[#026B07]/30 blur-3xl" />
            <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:70px_70px]" />

            <div className="relative z-10 grid lg:grid-cols-[1fr_auto] gap-10 items-center">
              <div className="reveal-up">
                <p className="text-[#F2AF11] font-black tracking-[0.2em] uppercase text-xs mb-5">
                  Central Portal Access
                </p>

                <h2
                  className="text-4xl md:text-5xl font-black text-white leading-tight"
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  Access the Engineering Dashboard Through the OGFZA Portal
                </h2>

                <p className="text-white/75 leading-[1.9] mt-5 max-w-3xl">
                  All service areas use one central login. After authentication,
                  Engineering users are directed to the Engineering dashboard
                  for applications, submissions, inspection requests and
                  tracking.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row lg:flex-col gap-4">
                <Link
                  to={portalLink}
                  className="inline-flex items-center justify-center gap-3 bg-[#F2AF11] text-[#082567] px-8 py-4 rounded-full font-black hover:bg-white transition-all duration-300"
                >
                  Open Engineering Portal
                  <ArrowUpRight size={18} />
                </Link>

                <Link
                  to="/services"
                  className="inline-flex items-center justify-center gap-3 bg-white/10 border border-white/20 text-white px-8 py-4 rounded-full font-black hover:bg-white hover:text-[#082567] transition-all duration-300"
                >
                  View Other Services
                  <ArrowUpRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Engineering;